import type { FirebaseApp } from 'firebase/app'
import type { Auth } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import type { AppCheck } from 'firebase/app-check'
import { nativeAppCheckProvider } from '~/utils/nativeAppCheck'
import { APPCHECK_DEBUG_TOKEN, IS_NATIVE, NATIVE_FIREBASE_APP_ID, PLATFORM } from '~/utils/platform'

/**
 * Firebase client singleton (v1.16 — accounts + leaderboard groundwork).
 * The config is public by design: Firebase web apps ship it in every
 * client bundle; real protection lives in Firestore security rules and
 * the Auth authorized-domains list.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyD02hsrhDfQdJBAxBYFDC9FPAqr3HzeoOI',
  authDomain: 'elemental-dispatch.firebaseapp.com',
  projectId: 'elemental-dispatch',
  storageBucket: 'elemental-dispatch.firebasestorage.app',
  messagingSenderId: '944294572418',
  // 📱 M3: an app bundle speaks as its own registered app (utils/platform.ts).
  appId: NATIVE_FIREBASE_APP_ID || '1:944294572418:web:ac5789943654697b66df9b',
  measurementId: 'G-XWY9BJBRVX',
}

/**
 * 🛡️ v6.40 (launch-hardening audit): App Check. Public like the config
 * above; the key is registered for elementaldispatch.com only, score-
 * based reCAPTCHA Enterprise, created by Javi on 2026-09-07. It attests
 * that Firestore and API calls come from the real page. Enforcement is
 * a separate switch: the console for Firestore, APP_CHECK_ENFORCE for
 * the API (server/middleware/02.app-check.ts) - both stay off until a
 * week of monitor logs shows honest clients all carry a token.
 */
const RECAPTCHA_ENTERPRISE_SITE_KEY = '6LeyRq0tAAAAAIlps1AFG1ce9elD_eBx-5qbkT1m'

/**
 * ⚡ v6.62 (Lighthouse pass 3): the SDK is a LAZY chunk. Every firebase/*
 * VALUE import in the app lives in bootFirebase() below as a dynamic
 * import - a static one anywhere would drag the ~400 KB SDK (plus the
 * reCAPTCHA and auth iframes it pulls in) back into the entry chunk and
 * onto the title's critical path. Callers reach the SDK's functions
 * through the modules on this handle.
 */
export interface Fb {
  app: FirebaseApp
  auth: Auth
  db: Firestore
  appCheck: AppCheck | null
  authMod: typeof import('firebase/auth')
  fs: typeof import('firebase/firestore')
  appCheckMod: typeof import('firebase/app-check')
}

let booting: Promise<Fb> | null = null
let resolveReady: (fb: Fb) => void = () => {}
let rejectReady: (error: unknown) => void = () => {}
const ready = new Promise<Fb>((resolve, reject) => { resolveReady = resolve; rejectReady = reject })
// Some callers start the boot before any lazy consumer waits for readiness.
// Observe a failure here while preserving the rejection for those consumers.
void ready.catch(() => {})

/** Load and initialise the SDK NOW. The plugin calls this on its boot
 * policy (a device that has seen a Google sign-in boots at once; anyone
 * else on the first gesture or eight seconds in); sign-in / sign-out
 * call it too. Client-only: the game runs entirely in the browser. */
export function bootFirebase(): Promise<Fb> {
  if (!import.meta.client) throw new Error('bootFirebase() is client-only')
  if (!booting) {
    booting = (async () => {
      const [appMod, authMod, fs, appCheckMod] = await Promise.all([
        import('firebase/app'), import('firebase/auth'), import('firebase/firestore'), import('firebase/app-check'),
      ])
      const app = appMod.initializeApp(firebaseConfig)
      let appCheck: AppCheck | null = null
      if (IS_NATIVE) {
        const { FirebaseAppCheck } = await import('@capacitor-firebase/app-check')
        // Native provider first, then its JS adapter, then Auth and Firestore.
        // Only debug bundles opt into the native debug provider. Release builds
        // contain no debug token and always use Play Integrity / App Attest.
        await FirebaseAppCheck.initialize({
          isTokenAutoRefreshEnabled: true,
          ...(APPCHECK_DEBUG_TOKEN ? { debugToken: true } : {}),
        })
        appCheck = appCheckMod.initializeAppCheck(app, {
          provider: new appCheckMod.CustomProvider(nativeAppCheckProvider(FirebaseAppCheck)),
          isTokenAutoRefreshEnabled: true,
        })
      } else if (RECAPTCHA_ENTERPRISE_SITE_KEY) {
        if (import.meta.dev) {
          (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: unknown }).FIREBASE_APPCHECK_DEBUG_TOKEN ??= true
        }
        try {
          appCheck = appCheckMod.initializeAppCheck(app, {
            provider: new appCheckMod.ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
            isTokenAutoRefreshEnabled: true,
          })
        } catch (error) {
          console.warn('App Check init failed — continuing without it.', error)
        }
      }
      const auth = IS_NATIVE
        ? authMod.initializeAuth(app, { persistence: authMod.indexedDBLocalPersistence })
        : authMod.getAuth(app)
      const db = PLATFORM === 'ios'
        ? fs.initializeFirestore(app, { experimentalForceLongPolling: true })
        : fs.getFirestore(app)
      const fb: Fb = { app, auth, db, appCheck, authMod, fs, appCheckMod }
      resolveReady(fb)
      return fb
    })().catch((error) => {
      rejectReady(error)
      throw error
    })
  }
  return booting
}

/** Resolves once the SDK is booted. It does NOT start the boot: a call
 * made before the plugin's policy fires simply waits for it (the wallet
 * refresh on the title, say, waits for the first tap or the timer). */
export function firebaseReady(): Promise<Fb> {
  return ready
}

/**
 * The App Check header for an API call. Fails OPEN on the client: a
 * reCAPTCHA outage must never strand the stardust outbox, the anonymous
 * wallet mint or the daily auto-pop. The server is the enforcer, and
 * only once APP_CHECK_ENFORCE is on.
 */
export async function appCheckHeader(): Promise<Record<string, string>> {
  const fb = await firebaseReady()
  if (!fb.appCheck) return {}
  try {
    const t = await fb.appCheckMod.getToken(fb.appCheck, false)
    return t.token ? { 'X-Firebase-AppCheck': t.token } : {}
  } catch { return {} }
}
